import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import JoditEditor from 'jodit-react';
import {
    Box,
    Button,
    FormControlLabel,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';
import { formatNoteEditorTimestamp } from '../utils/dateFormats';
import {
    normalizeJoditTempHtml,
    normalizeTxtEditorHtml,
    TXT_QUICK_COLOR_PALETTE,
    TxtSplitEditor,
    type TxtQuickColorKey,
    type TxtSplitEditorHandle,
} from './txt-editor';
import {
    isTxtQuickColorShortcutKey,
    TXT_QUICK_COLOR_KEY_MAP,
} from './txt-editor/txtEditorShortcuts';

export type RichHtmlEditorMode = 'jodit' | 'textarea';

type MemoJoditEditorProps = {
    value: string;
    config: Record<string, unknown>;
    onBlur: (content: string) => void;
    editorRef: React.Ref<unknown>;
};

const MemoJoditEditor = React.memo(({ value, config, onBlur, editorRef }: MemoJoditEditorProps) => (
    <JoditEditor
        // jodit-react ref typing is loose across versions
        ref={editorRef as any}
        value={value}
        config={config}
        onBlur={onBlur}
    />
));

const QUICK_COLOR_OPTIONS: Array<{ key: TxtQuickColorKey; label: string; hotkey: string }> = [
    { key: 'green', label: 'Green', hotkey: 'G' },
    { key: 'orange', label: 'Orange', hotkey: 'O' },
    { key: 'red', label: 'Red', hotkey: 'R' },
];

export type RichHtmlEditorFieldProps = {
    label?: string;
    helperText?: string;
    value: string;
    onChange: (value: string) => void;
    onBlur?: (value: string) => void | Promise<void>;
    disabled?: boolean;
    editorModeStorageKey: string;
    showHtmlSourceStorageKey: string;
    paneHeight?: number;
    joditMinHeight?: number;
    defaultEditorMode?: RichHtmlEditorMode;
    /** Show Insert time / color quick-action buttons above Jodit (keys work either way). */
    showJoditQuickActions?: boolean;
};

function readEditorMode(storageKey: string, fallback: RichHtmlEditorMode): RichHtmlEditorMode {
    try {
        const raw = localStorage.getItem(storageKey);
        if (raw === 'textarea' || raw === 'jodit') {
            return raw;
        }
    } catch {
        /* ignore */
    }
    return fallback;
}

function isFocusInsideJodit(active: HTMLElement | null): boolean {
    if (!active) {
        return false;
    }
    return !!(
        active.classList?.contains('jodit-wysiwyg') ||
        active.closest?.('.jodit-wysiwyg') ||
        active.closest?.('.jodit-container')
    );
}

function isFocusInsideThisJodit(active: HTMLElement | null, editor: any): boolean {
    if (!active || !editor) {
        return false;
    }
    const root: HTMLElement | null =
        (editor.container as HTMLElement | undefined) ??
        (editor.editor as HTMLElement | undefined)?.closest?.('.jodit-container') ??
        null;
    if (root) {
        return root.contains(active);
    }
    return isFocusInsideJodit(active);
}

export const RichHtmlEditorField = ({
    label,
    helperText,
    value,
    onChange,
    onBlur,
    disabled = false,
    editorModeStorageKey,
    showHtmlSourceStorageKey,
    paneHeight = 240,
    joditMinHeight = 240,
    defaultEditorMode = 'textarea',
    showJoditQuickActions = false,
}: RichHtmlEditorFieldProps) => {
    const editorRef = useRef<any>(null);
    const txtSplitEditorRef = useRef<TxtSplitEditorHandle | null>(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const [editorMode, setEditorMode] = useState<RichHtmlEditorMode>(() =>
        readEditorMode(editorModeStorageKey, defaultEditorMode),
    );

    const handleToggleEditorMode = useCallback(
        (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            const next: RichHtmlEditorMode = checked ? 'textarea' : 'jodit';
            setEditorMode(next);
            try {
                localStorage.setItem(editorModeStorageKey, next);
            } catch {
                /* ignore */
            }
        },
        [editorModeStorageKey],
    );

    const getJodit = useCallback(() => {
        const refValue = editorRef.current as any;
        if (refValue?.selection?.insertHTML) {
            return refValue;
        }
        return refValue?.editor ?? null;
    }, []);

    const syncFromJodit = useCallback(() => {
        const editor = getJodit();
        if (!editor) {
            return;
        }
        const raw = typeof editor.value === 'string' ? editor.value : '';
        onChangeRef.current(normalizeJoditTempHtml(raw));
    }, [getJodit]);

    const insertJoditTimestamp = useCallback(() => {
        const editor = getJodit();
        if (!editor?.selection?.insertHTML) {
            return;
        }
        const date = formatNoteEditorTimestamp();
        editor.selection.insertHTML(
            `<span style='color:#40E0D0; font-size:8px;'>${date}</span>`,
        );
        syncFromJodit();
    }, [getJodit, syncFromJodit]);

    const applyJoditInlineStyle = useCallback(
        (styleType: 'color' | 'background-color', color: string) => {
            const editor = getJodit();
            if (!editor?.selection?.insertHTML) {
                return;
            }

            const selection = editor.selection;
            const selectedHtml = typeof selection.html === 'function' ? selection.html() : '';
            const selectedText = selection.sel?.toString?.() ?? '';
            const content = (selectedHtml || selectedText).trim();
            const safeContent = content || 'text';
            editor.selection.insertHTML(`<span style="${styleType}: ${color};">${safeContent}</span>`);
            syncFromJodit();
        },
        [getJodit, syncFromJodit],
    );

    const applyJoditQuickColor = useCallback(
        (target: 'text' | 'background', colorKey: TxtQuickColorKey) => {
            if (target === 'text') {
                applyJoditInlineStyle('color', TXT_QUICK_COLOR_PALETTE.text[colorKey]);
                return;
            }
            applyJoditInlineStyle('background-color', TXT_QUICK_COLOR_PALETTE.background[colorKey]);
        },
        [applyJoditInlineStyle],
    );

    useEffect(() => {
        if (editorMode !== 'jodit' || disabled) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            const key = event.key.toLowerCase();
            const editor = getJodit();
            const active = document.activeElement as HTMLElement | null;
            if (!isFocusInsideThisJodit(active, editor)) {
                return;
            }

            const isInsertTimestamp =
                (event.altKey && !event.metaKey && !event.ctrlKey && key === 'q') ||
                (event.metaKey && !event.altKey && !event.ctrlKey && key === 'q');

            if (isInsertTimestamp) {
                event.preventDefault();
                event.stopPropagation();
                insertJoditTimestamp();
                return;
            }

            if (!event.altKey || event.metaKey || event.ctrlKey || !isTxtQuickColorShortcutKey(key)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            const colorKey = TXT_QUICK_COLOR_KEY_MAP[key];
            if (event.shiftKey) {
                applyJoditQuickColor('text', colorKey);
            } else {
                applyJoditQuickColor('background', colorKey);
            }
        };

        // Capture so parent window handlers (e.g. EA Notes) do not steal focus-targeted keys.
        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [applyJoditQuickColor, disabled, editorMode, getJodit, insertJoditTimestamp]);

    const joditConfig = useMemo(
        () => ({
            readonly: disabled,
            toolbar: true,
            spellcheck: true,
            language: 'en',
            toolbarButtonSize: 'middle' as const,
            toolbarAdaptive: false,
            showCharsCounter: false,
            showWordsCounter: false,
            showXPathInStatusbar: false,
            askBeforePasteHTML: false,
            askBeforePasteFromWord: false,
            defaultActionOnPaste: 'insert_clear_html' as const,
            buttons: [
                'source',
                '|',
                'bold',
                'italic',
                'underline',
                '|',
                'ul',
                'ol',
                '|',
                'font',
                'fontsize',
                'brush',
                'paragraph',
                '|',
                'link',
                'image',
                'table',
                '|',
                {
                    name: 'insertDate',
                    tooltip: 'Insert timestamp (Alt+Q)',
                    icon: '<svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8"/><path fill="currentColor" d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>',
                    exec: () => insertJoditTimestamp(),
                },
                '|',
                'fullsize',
            ],
            minHeight: joditMinHeight,
            maxWidth: '100%',
            style: {
                background: '#fff',
            },
        }),
        [disabled, insertJoditTimestamp, joditMinHeight],
    );

    const handleTxtChange = useCallback(
        (next: string) => {
            onChange(normalizeTxtEditorHtml(next ?? ''));
        },
        [onChange],
    );

    const handleTxtBlur = useCallback(
        (next: string) => {
            const normalized = normalizeTxtEditorHtml(next ?? '');
            onChange(normalized);
            return onBlur?.(normalized);
        },
        [onBlur, onChange],
    );

    const handleJoditBlur = useCallback(
        (content: string) => {
            const normalized = normalizeJoditTempHtml(content ?? '');
            onChange(normalized);
            void onBlur?.(normalized);
        },
        [onBlur, onChange],
    );

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 1.5,
                    mb: label || helperText ? 1 : 0.5,
                    flexWrap: 'wrap',
                }}
            >
                {label ? (
                    <Typography variant="subtitle1">{label}</Typography>
                ) : null}
                <Tooltip
                    title={
                        editorMode === 'textarea'
                            ? 'Plain TXT split editor. Switch off for Jodit (WYSIWYG HTML).'
                            : 'Jodit rich editor (HTML). Switch on for plain TXT.'
                    }
                >
                    <FormControlLabel
                        control={
                            <Switch
                                checked={editorMode === 'textarea'}
                                onChange={handleToggleEditorMode}
                                size="small"
                                color="primary"
                                disabled={disabled}
                            />
                        }
                        label={editorMode === 'textarea' ? 'TXT' : 'Jodit'}
                        sx={{ ml: 0, mr: 0, userSelect: 'none' }}
                    />
                </Tooltip>
            </Box>

            {editorMode === 'textarea' ? (
                <TxtSplitEditor
                    ref={txtSplitEditorRef}
                    value={value || ''}
                    onChange={handleTxtChange}
                    onBlur={handleTxtBlur}
                    disabled={disabled}
                    paneHeight={paneHeight}
                    layout="auto"
                    showHtmlSourceStorageKey={showHtmlSourceStorageKey}
                    showInsertTimestampButton
                    enableColorShortcuts
                />
            ) : (
                <Box>
                    {showJoditQuickActions ? (
                        <Stack spacing={1} sx={{ mb: 1.25 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="caption" sx={{ minWidth: 72, color: 'text.secondary' }}>
                                    Quick actions
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={insertJoditTimestamp}
                                    disabled={disabled}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Insert time (Alt+Q)
                                </Button>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="caption" sx={{ minWidth: 72, color: 'text.secondary' }}>
                                    Text color
                                </Typography>
                                {QUICK_COLOR_OPTIONS.map((item) => (
                                    <Button
                                        key={`text-${item.key}`}
                                        size="small"
                                        variant="outlined"
                                        disabled={disabled}
                                        onClick={() => applyJoditQuickColor('text', item.key)}
                                        startIcon={
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    bgcolor: TXT_QUICK_COLOR_PALETTE.text[item.key],
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            />
                                        }
                                        sx={{
                                            textTransform: 'none',
                                            color: TXT_QUICK_COLOR_PALETTE.text[item.key],
                                            borderColor: TXT_QUICK_COLOR_PALETTE.text[item.key],
                                        }}
                                    >
                                        {item.label} (Alt+Shift+{item.hotkey})
                                    </Button>
                                ))}
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="caption" sx={{ minWidth: 72, color: 'text.secondary' }}>
                                    Background
                                </Typography>
                                {QUICK_COLOR_OPTIONS.map((item) => (
                                    <Button
                                        key={`background-${item.key}`}
                                        size="small"
                                        variant="outlined"
                                        disabled={disabled}
                                        onClick={() => applyJoditQuickColor('background', item.key)}
                                        startIcon={
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    bgcolor: TXT_QUICK_COLOR_PALETTE.background[item.key],
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            />
                                        }
                                        sx={{
                                            textTransform: 'none',
                                            bgcolor: TXT_QUICK_COLOR_PALETTE.background[item.key],
                                            borderColor: TXT_QUICK_COLOR_PALETTE.text[item.key],
                                            color: '#1f1f1f',
                                            '&:hover': {
                                                bgcolor: TXT_QUICK_COLOR_PALETTE.background[item.key],
                                                filter: 'brightness(0.98)',
                                            },
                                        }}
                                    >
                                        {item.label} (Alt+{item.hotkey})
                                    </Button>
                                ))}
                            </Stack>
                        </Stack>
                    ) : null}
                    <MemoJoditEditor
                        editorRef={editorRef}
                        value={value || ''}
                        config={joditConfig}
                        onBlur={handleJoditBlur}
                    />
                </Box>
            )}

            {helperText ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {helperText}
                </Typography>
            ) : null}
        </Box>
    );
};
